import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Input,
  Text,
  VStack
} from "@chakra-ui/react";
import { useState } from "react";
import { FiBarChart2, FiCalendar } from "react-icons/fi";
import TrafficMap from "../components/TrafficMap";

interface Country {
  name: string;
  visits: number;
  percentage: number;
}

interface ViewStats {
  totalViews: number;
  pageViews: number;
  botViews: number;
  adViews: number;
  firstTimeViews: number;
}

function Traffic() {
  // Set default dates to today
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reportData, setReportData] = useState<any>(null);
  const [selectedRange, setSelectedRange] = useState<string>("today");

  // Mock view statistics
  const [viewStats] = useState<ViewStats>({
    totalViews: 128495,
    pageViews: 89234,
    botViews: 12567,
    adViews: 15678,
    firstTimeViews: 45234
  });

  // Helper function to set date ranges
  const setDateRange = (range: string) => {
    const today = new Date();
    let endDateObj = new Date(today);
    let startDateObj = new Date(today);

    switch (range) {
      case "today":
        // Both dates are today
        break;
      case "yesterday":
        startDateObj.setDate(today.getDate() - 1);
        endDateObj.setDate(today.getDate() - 1);
        break;
      case "last7":
        startDateObj.setDate(today.getDate() - 6);
        break;
      case "last30":
        startDateObj.setDate(today.getDate() - 29);
        break;
      case "last90":
        startDateObj.setDate(today.getDate() - 89);
        break;
      case "thisMonth":
        startDateObj = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "lastMonth":
        startDateObj = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDateObj = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    setStartDate(startDateObj.toISOString().split("T")[0]);
    setEndDate(endDateObj.toISOString().split("T")[0]);
    setSelectedRange(range);
  };

  // Mock data for Toronto-focused map
  const mockData = {
    countries: [
      { name: "Downtown Toronto", visits: 45234, percentage: 35.2 },
      { name: "Mississauga", visits: 23891, percentage: 18.6 },
      { name: "North York", visits: 18567, percentage: 14.4 },
      { name: "Markham", visits: 12340, percentage: 9.6 },
      { name: "Brampton", visits: 9876, percentage: 7.7 }
    ]
  };

  const handleGenerateReport = () => {
    console.log(`Generating traffic report for:`, startDate, "to", endDate);

    // Simulate API call with mock data
    setTimeout(() => {
      setReportData(mockData);
    }, 500);
  };

  const renderMapContent = () => {
    if (!reportData || !reportData.countries) return null;

    // Convert country data to traffic points for the map
    const trafficData = reportData.countries.map((country: Country) => ({
      country: country.name,
      lat: getCountryCoordinates(country.name).lat,
      lng: getCountryCoordinates(country.name).lng,
      intensity: country.percentage / 100,
      visits: country.visits
    }));

    return (
      <Box>
        <Heading size="md" color="#191d16" mb={6}>
          Global Traffic Map
        </Heading>

        {/* Map Preview */}
        <Box mb={8}>
          <TrafficMap data={trafficData} />
        </Box>
      </Box>
    );
  };

  return (
    <Container maxW="7xl" py={8}>
      {/* Header */}
      <Box mb={8}>
        <Heading size="2xl" color="#191d16" mb={2}>
          Traffic
        </Heading>
      </Box>

      {/* Date Range Picker - Improved UI */}
      <Box
        bg="white"
        py={8}
        px={12}
        rounded="2xl"
        shadow="xl"
        border="1px"
        borderColor="gray.100"
        maxW="900px"
        mx="auto"
        mb={8}
        transition="all 0.3s ease"
        _hover={{
          shadow: "2xl",
          transform: "translateY(-2px)"
        }}
      >
        <VStack gap={6}>
          {/* Header */}
          <HStack gap={3}>
            <Icon as={FiBarChart2} w={6} h={6} color="#0959ED" />
            <Heading size="md" color="#191d16">
              Generate Traffic Report
            </Heading>
          </HStack>

          {/* Quick Date Range Buttons */}
          <VStack gap={4} w="full">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Quick Select
            </Text>
            <ButtonGroup size="sm" variant="outline" gap={3}>
              <Button
                onClick={() => setDateRange("today")}
                bg={selectedRange === "today" ? "#0959ED" : "white"}
                color={selectedRange === "today" ? "white" : "gray.700"}
                borderColor={selectedRange === "today" ? "#0959ED" : "gray.300"}
                transition="all 0.2s ease"
                transform={
                  selectedRange === "today" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "today" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Today
              </Button>
              <Button
                onClick={() => setDateRange("yesterday")}
                bg={selectedRange === "yesterday" ? "#0959ED" : "white"}
                color={selectedRange === "yesterday" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "yesterday" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "yesterday" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "yesterday" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Yesterday
              </Button>
              <Button
                onClick={() => setDateRange("last7")}
                bg={selectedRange === "last7" ? "#0959ED" : "white"}
                color={selectedRange === "last7" ? "white" : "gray.700"}
                borderColor={selectedRange === "last7" ? "#0959ED" : "gray.300"}
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last7" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last7" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 7 Days
              </Button>
              <Button
                onClick={() => setDateRange("last30")}
                bg={selectedRange === "last30" ? "#0959ED" : "white"}
                color={selectedRange === "last30" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "last30" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last30" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last30" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 30 Days
              </Button>
              <Button
                onClick={() => setDateRange("last90")}
                bg={selectedRange === "last90" ? "#0959ED" : "white"}
                color={selectedRange === "last90" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "last90" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "last90" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "last90" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last 90 Days
              </Button>
              <Button
                onClick={() => setDateRange("thisMonth")}
                bg={selectedRange === "thisMonth" ? "#0959ED" : "white"}
                color={selectedRange === "thisMonth" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "thisMonth" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "thisMonth" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "thisMonth" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                This Month
              </Button>
              <Button
                onClick={() => setDateRange("lastMonth")}
                bg={selectedRange === "lastMonth" ? "#0959ED" : "white"}
                color={selectedRange === "lastMonth" ? "white" : "gray.700"}
                borderColor={
                  selectedRange === "lastMonth" ? "#0959ED" : "gray.300"
                }
                transition="all 0.2s ease"
                transform={
                  selectedRange === "lastMonth" ? "scale(1.05)" : "scale(1)"
                }
                _hover={{
                  bg: selectedRange === "lastMonth" ? "#0849D1" : "gray.50",
                  borderColor: "#0959ED",
                  transform: "scale(1.05)"
                }}
                _active={{
                  transform: "scale(0.95)"
                }}
              >
                Last Month
              </Button>
            </ButtonGroup>
          </VStack>

          <Box w="full" h="1px" bg="gray.200" />

          {/* Custom Date Range */}
          <VStack gap={4} w="full">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Custom Range
            </Text>
            <HStack gap={4} justify="center">
              <VStack align="start" gap={2}>
                <HStack gap={2}>
                  <Icon as={FiCalendar} w={4} h={4} color="gray.500" />
                  <Text fontSize="sm" fontWeight="500" color="gray.700">
                    From
                  </Text>
                </HStack>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setSelectedRange("custom");
                  }}
                  size="md"
                  width="180px"
                  borderColor="gray.300"
                  borderRadius="lg"
                  bg="gray.50"
                  _hover={{ borderColor: "#0959ED", bg: "white" }}
                  _focus={{
                    borderColor: "#0959ED",
                    boxShadow: "0 0 0 1px #0959ED",
                    bg: "white"
                  }}
                />
              </VStack>

              <Box pt={6}>
                <Text color="gray.400" fontSize="lg">
                  →
                </Text>
              </Box>

              <VStack align="start" gap={2}>
                <HStack gap={2}>
                  <Icon as={FiCalendar} w={4} h={4} color="gray.500" />
                  <Text fontSize="sm" fontWeight="500" color="gray.700">
                    To
                  </Text>
                </HStack>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setSelectedRange("custom");
                  }}
                  size="md"
                  width="180px"
                  borderColor="gray.300"
                  borderRadius="lg"
                  bg="gray.50"
                  _hover={{ borderColor: "#0959ED", bg: "white" }}
                  _focus={{
                    borderColor: "#0959ED",
                    boxShadow: "0 0 0 1px #0959ED",
                    bg: "white"
                  }}
                />
              </VStack>
            </HStack>

            {/* Generate Button */}
            <Button
              bg="#0959ED"
              color="white"
              size="lg"
              px={12}
              py={6}
              onClick={handleGenerateReport}
              borderRadius="xl"
              fontWeight="600"
              letterSpacing="wide"
              _hover={{
                bg: "#0849D1",
                transform: "translateY(-2px)",
                shadow: "2xl"
              }}
              _active={{
                bg: "#0739B8",
                transform: "translateY(0px)"
              }}
              disabled={
                !startDate ||
                !endDate ||
                new Date(startDate) > new Date(endDate)
              }
              _disabled={{
                bg: "gray.300",
                color: "gray.500",
                cursor: "not-allowed",
                _hover: { bg: "gray.300", transform: "none", shadow: "none" }
              }}
              w="full"
              maxW="300px"
            >
              <HStack gap={2}>
                <Icon as={FiBarChart2} w={5} h={5} />
                <Text>Generate Report</Text>
              </HStack>
            </Button>

            {/* Error message for invalid date range */}
            {startDate &&
              endDate &&
              new Date(startDate) > new Date(endDate) && (
                <Text fontSize="sm" color="red.500">
                  Start date must be before or equal to end date
                </Text>
              )}
          </VStack>
        </VStack>
      </Box>

      {/* View Statistics */}
      <Grid templateColumns="repeat(5, 1fr)" gap={4} mb={8}>
        <GridItem>
          <Box
            bg="white"
            p={6}
            rounded="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
              Total Views
            </Text>
            <Text color="#191d16" fontSize="2xl" fontWeight="700" mb={1}>
              {viewStats.totalViews.toLocaleString()}
            </Text>
            <Text color="gray.500" fontSize="xs">
              All traffic combined
            </Text>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            p={6}
            rounded="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
              Page Views
            </Text>
            <Text color="#0959ED" fontSize="2xl" fontWeight="700" mb={1}>
              {viewStats.pageViews.toLocaleString()}
            </Text>
            <Text color="gray.500" fontSize="xs">
              Unique page visits
            </Text>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            p={6}
            rounded="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
              Bot Views
            </Text>
            <Text color="#F59E0B" fontSize="2xl" fontWeight="700" mb={1}>
              {viewStats.botViews.toLocaleString()}
            </Text>
            <Text color="gray.500" fontSize="xs">
              Automated traffic
            </Text>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            p={6}
            rounded="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
              Ad Views
            </Text>
            <Text color="#10B981" fontSize="2xl" fontWeight="700" mb={1}>
              {viewStats.adViews.toLocaleString()}
            </Text>
            <Text color="gray.500" fontSize="xs">
              Advertisement clicks
            </Text>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            p={6}
            rounded="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
              First Time Views
            </Text>
            <Text color="#8B5CF6" fontSize="2xl" fontWeight="700" mb={1}>
              {viewStats.firstTimeViews.toLocaleString()}
            </Text>
            <Text color="gray.500" fontSize="xs">
              New visitors
            </Text>
          </Box>
        </GridItem>
      </Grid>

      {/* Map Content */}
      <Box>
        {reportData ? (
          renderMapContent()
        ) : (
          <Box>
            <Heading size="md" color="#191d16" mb={6}>
              Global Traffic Map
            </Heading>

            {/* Map Preview */}
            <Box mb={8}>
              <TrafficMap />
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
}

// Helper function to get Toronto area coordinates
const getCountryCoordinates = (countryName: string) => {
  const coordinates: { [key: string]: { lat: number; lng: number } } = {
    "Downtown Toronto": { lat: 43.6532, lng: -79.3832 },
    "Mississauga": { lat: 43.5890, lng: -79.6441 },
    "North York": { lat: 43.7615, lng: -79.4111 },
    "Markham": { lat: 43.8561, lng: -79.3370 },
    "Brampton": { lat: 43.7315, lng: -79.7624 },
    "Richmond Hill": { lat: 43.8828, lng: -79.4403 },
    "Vaughan": { lat: 43.8361, lng: -79.4985 },
    "Oakville": { lat: 43.4675, lng: -79.6877 },
    "Burlington": { lat: 43.3255, lng: -79.7990 },
    "Ajax": { lat: 43.8509, lng: -79.0204 },
    "Pickering": { lat: 43.8384, lng: -79.0868 },
    "Whitby": { lat: 43.8975, lng: -78.9429 },
    "Yorkville": { lat: 43.6708, lng: -79.3899 },
    "Liberty Village": { lat: 43.6368, lng: -79.4197 },
    "King West": { lat: 43.6441, lng: -79.3957 }
  };

  return coordinates[countryName] || { lat: 43.6532, lng: -79.3832 };
};

export default Traffic;
